import {
  createStyles,
  makeStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ErrorStatus, SuccessStatus, WarningStatus } from '../../../../icons/StatusIcons';
import { QualityScore } from '../Quality';
import { useToolTipContent } from '../../ToolTipsInformation/UseToolTipContent';
import { InfoIcon } from '../../../../icons/InfoIcon';
import { t } from 'i18next';

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      width: '100%',
    },
    content: {
      padding: '3em',
      '@media screen and (max-width: 760px)': {
        padding: '0px',
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      '& div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& svg': {
        marginRight: '0.3em',
      },
    },
    typography: {
      '@media screen and (max-width: 760px) and (min-width: 301px)': {
        width: '235px',
      },
      '@media screen and (max-width: 300px)': {
        width: '150px',
      },
    },
  })
);

interface QualityModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  bitrateUDP: { average?: string; max?: string; min?: string; qualityScore: QualityScore };
  bitrateTCP: { average?: string; max?: string; min?: string; qualityScore: QualityScore };
  isTCPEnabled: boolean;
  downloadSpeed: number | null;
  uploadSpeed: number | null;
  rttResult: number | null;
  jitterResult: number | null;
}

export function QualityModal({
  isModalOpen,
  setIsModalOpen,
  bitrateUDP,
  bitrateTCP,
  isTCPEnabled,
  downloadSpeed,
  uploadSpeed,
  rttResult,
  jitterResult,
}: QualityModalProps) {
  const statusIcons = {
    [QualityScore.Excellent]: <SuccessStatus />,
    [QualityScore.Good]: <SuccessStatus />,
    [QualityScore.Suboptimal]: <WarningStatus />,
    [QualityScore.Poor]: <ErrorStatus />,
  };
  const classes = useStyles();
  const toolTipContent = useToolTipContent();

  const getTranslatedScore = (score: QualityScore): string => {
    const scoreMap = {
      [QualityScore.Poor]: t('SPEED.POOR'),
      [QualityScore.Suboptimal]: t('SPEED.SUBOPTIMAL'),
      [QualityScore.Good]: t('SPEED.GOOD'),
      [QualityScore.Excellent]: t('SPEED.EXCELLENT'),
    };

    return scoreMap[score];
  };

  return (
    <>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} classes={{ paper: classes.modal }}>
        <DialogTitle disableTypography>
          <Typography variant="h3" className={classes.typography}>
            {t('SPEED.QUALITY_INFORMATION')}
          </Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">
                    <strong>{t('SPEED.NETWORK_INFO')}</strong>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    <strong>{t('SPEED.SCORE')}</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">
                      <strong>{t('SPEED.BITRATE_UDP_(KBPS)_AVG/MAX')}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.iconContainer}>
                    {statusIcons[bitrateUDP.qualityScore]}
                    <Typography variant="body1">
                      {`${bitrateUDP.average} / ${bitrateUDP.max} 
                    (${getTranslatedScore(bitrateUDP.qualityScore)})`}
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">
                      <strong>{t('SPEED.DOWNLOAD_SPEED')}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">
                      <strong>{downloadSpeed} mbps</strong>
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">
                      <strong>{t('SPEED.UPLOAD_SPEED')}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">
                      <strong>{uploadSpeed} mbps</strong>
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">
                      <strong>{t('SPEED.RTT_RESULT')}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">
                      <strong>{rttResult} ms</strong>
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">
                      <strong>{t('SPEED.JITTER_RESULT')}</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">
                      <strong>{jitterResult} ms</strong>
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
              {isTCPEnabled && (
                <TableRow>
                  <TableCell>
                    <div className={classes.iconContainer}>
                      <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                        <div>
                          <InfoIcon />
                        </div>
                      </Tooltip>
                      <Typography variant="body1">
                        <strong>{t('SPEED.BITRATE_TCP_(KBPS)_AVG/MAX')}</strong>
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={classes.iconContainer}>
                      {statusIcons[bitrateTCP.qualityScore]}
                      <Typography variant="body1">
                        {`${bitrateTCP.average} / ${bitrateTCP.max} 
                      (${getTranslatedScore(bitrateUDP.qualityScore)})`}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}
